import React from 'react';
import { Box } from '../Box/Box';
import { Icon } from '../Icon/Icon';
import { Tooltip } from '../Tooltip/Tooltip';
import { Breadcrumb } from './components/Breadcrumb/Breadcrumb';
import { OverflowMenu } from './components/OverflowMenu/OverflowMenu';
import { BreadcrumbsItem } from './Breadcrumbs.types';
import { useOverflowingItemsCount } from './hooks/useOverflowingItemsCount';
import { MAX_ITEM_TITLE_LENGTH } from './Breadcrumbs.constants';
import { truncateText } from '../../../CommonUtils/truncateText';

export interface BreadcrumbsProps {
  items: BreadcrumbsItem[];
  onNavigate(item: BreadcrumbsItem): void;
}

const BreadcrumbsComponent: React.FC<BreadcrumbsProps> = ({ items, onNavigate }) => {
  const { ref, overflowingItemsCount } = useOverflowingItemsCount();

  return (
    <Box as="nav" aria-label="breadcrumb" ref={ref}>
      <Box as="ol" display="flex" alignItems="center" p="0" css="list-style-type: none">
        {items.map((item, index) => {
          const isLast = items.length === index + 1;

          // We always show the first item and all items with an index bigger
          // than the number of items we should hide
          const showItem = index === 0 || index > overflowingItemsCount;

          // Hide all breadcrumbs that shouldn't be shown except in the second position (index === 1)
          // In the second position we render the Overflow menu (see below)
          if (!showItem && index !== 1) {
            return null;
          }

          return (
            <Box key={item.id} as="li" display="flex" alignItems="center" flexShrink={0}>
              {overflowingItemsCount > 0 && index === 1 ? (
                <OverflowMenu
                  items={items.slice(1, overflowingItemsCount + 1)}
                  onNavigate={onNavigate}
                />
              ) : (
                <Tooltip
                  content={item.title}
                  delay={[1000, 0]}
                  disabled={item.title.length <= MAX_ITEM_TITLE_LENGTH}
                  placement="top"
                >
                  <Breadcrumb
                    isActive={isLast}
                    onClick={() => onNavigate(item)}
                    tabIndex={isLast ? -1 : 0}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {truncateText(item.title, MAX_ITEM_TITLE_LENGTH)}
                  </Breadcrumb>
                </Tooltip>
              )}

              {!isLast && (
                <Icon
                  className="far fa-angle-right"
                  color="neutral.400"
                  mx="4"
                  role="presentation"
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * We need to remount the Breadcrumbs component when items change because we need to determine
 * if it is overflowing so we can truncate it accordingly
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = props => {
  // eslint-disable-next-line react/destructuring-assignment
  return <BreadcrumbsComponent key={JSON.stringify(props.items)} {...props} />;
};
